import type { PropsWithChildren } from 'react';
import { type ClassName, twx } from '@/utils/tailwind';

export const cardTones = ['surface', 'focal', 'subtle', 'inverse'] as const;
export type CardTone = (typeof cardTones)[number];

const toneClasses: Record<CardTone, string> = {
  // Plain white surface — the most common card body in the app.
  surface: 'bg-white border border-content-border',
  // Slightly tinted background used for grouping containers (Logistics,
  // EnRoute, the saved-trips wrapper in the trip selector).
  focal: 'bg-content-focal/50 border border-content-border',
  // Lavender-tinted, used for "selected" state rows.
  subtle: 'bg-primary-subtle border border-primary-main',
  // Translucent dark used over the kawaii gradient header.
  inverse: 'bg-secondary-focal/60 backdrop-blur-md border border-white/15 text-white',
};

export const cardPaddings = ['none', 'xs', 'sm', 'base', 'lg'] as const;
export type CardPadding = (typeof cardPaddings)[number];

const paddingClasses: Record<CardPadding, string> = {
  none: '',
  xs: 'p-1.5',
  sm: 'p-2',
  base: 'p-3',
  lg: 'p-4',
};

export interface CardProps extends PropsWithChildren {
  tone?: CardTone;
  padding?: CardPadding;
  /** Renders as a button when an onClick is provided. */
  onClick?: () => void;
  /** Accessible label, passed through to the underlying `<button>` when
   * `onClick` is set. Use standard React aria-label naming for parity with
   * native HTML and the rest of the design system. */
  'aria-label'?: string;
  /** Opt in to a hover-fill affordance. Only applies when `onClick` is also
   * set — non-interactive cards never hover-fill. Default off: a click
   * handler alone shouldn't change the visual chrome unless the call site
   * explicitly wants a press affordance. */
  hoverable?: boolean;
  className?: ClassName;
}

/**
 * Bordered surface with a tone-controlled background. The most pervasive
 * container shape in the app: trip selector rows, logistics groups, the
 * place-detail body, the scouted list's outer aside, etc.
 *
 * Doesn't enforce rounding — Tailwind `rounded-*` classes can be added via
 * `className` so call sites pick the right corner radius for their context.
 */
export const Card = ({
  tone = 'surface',
  padding = 'none',
  onClick,
  'aria-label': ariaLabel,
  hoverable = false,
  className,
  children,
}: CardProps) => {
  const classes = twx(
    'rounded-xl overflow-hidden transition-colors',
    toneClasses[tone],
    paddingClasses[padding],
    onClick && 'cursor-pointer',
    onClick && hoverable && 'hover:bg-content-background',
    className,
  );
  if (onClick) {
    return (
      <button type="button" onClick={onClick} aria-label={ariaLabel} className={classes}>
        {children}
      </button>
    );
  }
  return <div className={classes}>{children}</div>;
};

/** Compound slot: header band for a card. Mirrors `Modal.Header` — same
 *  border-bottom + tight padding so card-headers across the app stay
 *  visually aligned. */
const Header = ({ className, children }: { className?: ClassName; children: React.ReactNode }) => (
  <div className={twx('px-3 py-2.5 border-b border-content-divider', className)}>{children}</div>
);

/** Compound slot: main body of a card. Default `p-3` matches the
 *  `padding="base"` constant; pass `className="p-0"` to remove. */
const Body = ({ className, children }: { className?: ClassName; children: React.ReactNode }) => (
  <div className={twx('p-3', className)}>{children}</div>
);

/** Compound slot: bottom action row. Mirrors `Modal.Footer`. */
const Footer = ({ className, children }: { className?: ClassName; children: React.ReactNode }) => (
  <div
    className={twx(
      'flex items-center justify-end gap-2 px-3 py-2.5 bg-content-focal/40 border-t border-content-divider',
      className,
    )}
  >
    {children}
  </div>
);

// Compound exports for the canonical header/body/footer card layout.
Card.Header = Header;
Card.Body = Body;
Card.Footer = Footer;
